/**
 * Systemmeldungen für Dinge, die nicht warten können.
 *
 * ## Wofür das da ist
 *
 * Der Ton in der Seite (`ton.ts`) erreicht nur, wer die App gerade offen hat.
 * Liegt das Telefon mit dunklem Bildschirm daneben oder ist der Browser in
 * einem anderen Tab, hört niemand etwas — und genau dann kommt der Alarm, auf
 * den es ankommt.
 *
 * Eine Systemmeldung geht diesen zweiten Weg: Sie klingelt mit dem Ton des
 * Geräts, sie erscheint über allem, und sie ist der einzige Weg, der auch
 * dann noch trägt, wenn der Browser die Seite gedrosselt hat.
 *
 * ## Was sie nicht ist
 *
 * Kein Ersatz für Push. Push weckt ein Gerät, dessen App **geschlossen** ist —
 * das kann nur der Server, und für SOS tut er es auch. Diese Datei füllt die
 * Lücke dazwischen: App läuft, aber niemand schaut hin.
 *
 * ## Warum nur bei verdecktem Bildschirm
 *
 * Wer die App vor sich hat, sieht die Kachel und hört den Ton. Eine Meldung
 * obendrauf wäre dieselbe Nachricht ein zweites Mal — beim Anruf. Beim
 * **SOS** wird sie trotzdem immer gezeigt: Dort ist Doppelung das kleinere
 * Übel.
 */

interface Aufbau {
  titel: string
  text: string
  /** Gleiche Marke ersetzt eine frühere Meldung, statt sie zu stapeln. */
  marke: string
  /** SOS bleibt stehen, bis jemand sie wegtippt. */
  bleibt?: boolean
}

let offen: Notification | null = null

function darfMelden(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

/**
 * Zeigt eine Meldung — oder tut nichts.
 *
 * Wirft nie. Ohne Erlaubnis, ohne Unterstützung, bei einem Fehler im Browser:
 * Es bleibt bei dem, was die Seite selbst kann.
 */
export function melden(aufbau: Aufbau): void {
  if (!darfMelden()) return
  try {
    schliessen()
    offen = new Notification(aufbau.titel, {
      body: aufbau.text,
      tag: aufbau.marke,
      icon: '/icon-192.png',
      requireInteraction: aufbau.bleibt ?? false,
      // Ein Alarm, der stumm ankommt, ist kein Alarm.
      silent: false,
    })
    offen.onclick = () => {
      window.focus()
      schliessen()
    }
  } catch {
    // Manche Browser erlauben `new Notification` nur aus dem Service Worker.
    // Dann bleibt es beim Ton in der Seite — kein Grund, hier zu scheitern.
    offen = null
  }
}

/** Nimmt die zuletzt gezeigte Meldung weg. */
export function schliessen(): void {
  try {
    offen?.close()
  } catch {
    // Schon weg.
  }
  offen = null
}

/** Liegt die Seite im Hintergrund? Dann trägt nur die Systemmeldung. */
export function verdeckt(): boolean {
  return typeof document !== 'undefined' && document.hidden
}
